import { createContext, useContext, useState, useCallback, ReactNode } from 'react';

/**
 * Super-admin "browse as gym" (impersonation) state.
 *
 * When active, useApi attaches X-Impersonate-Gym-Id (and X-Impersonate-Edit)
 * to CRM API calls, and the backend re-maps the request to that gym's owner.
 * Persisted in localStorage so a page refresh keeps the session, and read
 * synchronously on init so the very first API call carries the header.
 */
export interface ImpersonationState {
  gymId: string;
  gymName: string;
  /** When false (default) the backend blocks writes — read-only browsing. */
  edit: boolean;
}

interface ImpersonationContextValue {
  impersonation: ImpersonationState | null;
  start: (gymId: string, gymName: string) => void;
  stop: () => void;
  setEdit: (edit: boolean) => void;
}

const STORAGE_KEY = 'impersonation';

function read(): ImpersonationState | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.gymId) return null;
    return { gymId: parsed.gymId, gymName: parsed.gymName || 'Gym', edit: !!parsed.edit };
  } catch {
    return null;
  }
}

function write(state: ImpersonationState | null) {
  try {
    if (state) localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    else localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore quota/availability errors */
  }
}

const ImpersonationContext = createContext<ImpersonationContextValue | undefined>(undefined);

export function ImpersonationProvider({ children }: { children: ReactNode }) {
  const [impersonation, setImpersonation] = useState<ImpersonationState | null>(() => read());

  const start = useCallback((gymId: string, gymName: string) => {
    const next = { gymId, gymName, edit: false };
    write(next);
    setImpersonation(next);
  }, []);

  const stop = useCallback(() => {
    write(null);
    setImpersonation(null);
  }, []);

  const setEdit = useCallback((edit: boolean) => {
    setImpersonation(prev => {
      if (!prev) return prev;
      const next = { ...prev, edit };
      write(next);
      return next;
    });
  }, []);

  return (
    <ImpersonationContext.Provider value={{ impersonation, start, stop, setEdit }}>
      {children}
    </ImpersonationContext.Provider>
  );
}

export function useImpersonation(): ImpersonationContextValue {
  const ctx = useContext(ImpersonationContext);
  if (!ctx) throw new Error('useImpersonation must be used within an ImpersonationProvider');
  return ctx;
}
